"use client";

import { c as _c } from "react/compiler-runtime";
import { useRef, useState } from "react";
import { useLocale, useTranslations } from "next-intl";
import { InstantSearchNext } from "react-instantsearch-nextjs";
import { Configure } from "react-instantsearch";
import Image from "next/image";
import { Link } from "@/i18n/navigation";
import { useNavigationState } from "@/components/navigation/navigation-provider";
import { useHomeHref } from "@/components/navigation/use-home-href";
import { useAudience } from "@/components/common/audience";
import { searchClient } from "@/lib/algolia/client";
import { excludePastEventsFilter, getAlgoliaIndexName } from "@/lib/algolia/config";
import { consentGatedInsights } from "@/lib/algolia/insights-client";
import { SearchInput } from "./search-input.component";
import { SearchSuggestions } from "./search-suggestions.component";
import { SearchAutocomplete } from "./search-autocomplete.component";
import { SearchBoxConnector } from "./search-box-connector.component";
import { useSearchOverlay } from "./use-search-overlay";
import { useViewportSafeHeight } from "./hooks/use-viewport-safe-height";
export function SearchOverlay() {
  const $ = _c(1);
  const {
    isSearchOpen
  } = useNavigationState();
  if (!isSearchOpen) {
    return null;
  }
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <SearchOverlayBody />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
function SearchOverlayBody() {
  const $ = _c(59);
  const t = useTranslations("search");
  const {
    navigationData
  } = useNavigationState();
  const locale = useLocale();
  const overlayRef = useRef(null);
  const contentRef = useRef(null);
  const [nowUnix] = useState(_temp);
  const contentMaxHeight = useViewportSafeHeight(contentRef);
  let t0;
  if ($[0] !== locale) {
    t0 = getAlgoliaIndexName(locale);
    $[0] = locale;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const indexName = t0;
  const searchSettings = navigationData.searchSettings;
  const logo = navigationData.logo;
  const {
    activeTab
  } = useAudience();
  const homeHref = useHomeHref();
  const isBusinesses = activeTab === "businesses";
  const logoSrc = isBusinesses ? logo?.dark ?? logo?.light ?? "/images/va-waterfront-logo-light.svg" : logo?.light ?? "/images/va-waterfront-logo.svg";
  const logoAlt = logo?.alt ?? "V&A Waterfront Cape Town";
  const {
    query,
    setQuery,
    searchHistory,
    handleClose,
    handleSubmit,
    handleSelectResult,
    handleSearchTerm,
    handleBackdropClick
  } = useSearchOverlay();
  const hasQuery = query.trim().length > 0;
  let t1;
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      insightsClient: consentGatedInsights
    };
    t2 = {
      preserveSharedStateOnUnmount: true
    };
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  let t3;
  if ($[4] !== nowUnix) {
    t3 = excludePastEventsFilter(nowUnix);
    $[4] = nowUnix;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t3) {
    t4 = <Configure filters={t3} />;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== handleBackdropClick) {
    t5 = <div className="fixed inset-0 top-[var(--navbar-height,155px)] z-40 bg-black/30" aria-hidden="true" onClick={handleBackdropClick} />;
    $[8] = handleBackdropClick;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== t) {
    t6 = t("inputAriaLabel");
    $[10] = t;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== logoAlt || $[13] !== logoSrc) {
    t7 = <Image src={logoSrc} alt={logoAlt} width={179} height={48} className="h-12 w-[179px]" />;
    $[12] = logoAlt;
    $[13] = logoSrc;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== handleClose || $[16] !== homeHref || $[17] !== t7) {
    t8 = <Link href={homeHref} className="flex-shrink-0" onClick={handleClose}>{t7}</Link>;
    $[15] = handleClose;
    $[16] = homeHref;
    $[17] = t7;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  const t9 = searchSettings?.searchPlaceholder ?? undefined;
  let t10;
  if ($[19] !== handleClose || $[20] !== handleSubmit || $[21] !== query || $[22] !== setQuery || $[23] !== t9) {
    t10 = <SearchInput value={query} onChange={setQuery} onSubmit={handleSubmit} onClose={handleClose} placeholder={t9} />;
    $[19] = handleClose;
    $[20] = handleSubmit;
    $[21] = query;
    $[22] = setQuery;
    $[23] = t9;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  let t11;
  if ($[25] !== t10 || $[26] !== t8) {
    t11 = <div className="hidden tablet:flex items-center gap-9 px-12 py-6 h-[96px]">{t8}{t10}</div>;
    $[25] = t10;
    $[26] = t8;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  const t12 = searchSettings?.searchPlaceholder ?? undefined;
  let t13;
  if ($[28] !== handleClose || $[29] !== handleSubmit || $[30] !== query || $[31] !== setQuery || $[32] !== t12) {
    t13 = <div className="flex tablet:hidden items-center gap-3 px-4 py-3 h-16"><SearchInput value={query} onChange={setQuery} onSubmit={handleSubmit} onClose={handleClose} placeholder={t12} /></div>;
    $[28] = handleClose;
    $[29] = handleSubmit;
    $[30] = query;
    $[31] = setQuery;
    $[32] = t12;
    $[33] = t13;
  } else {
    t13 = $[33];
  }
  let t14;
  if ($[34] !== t11 || $[35] !== t13) {
    t14 = <div className="border-b border-border-subtle">{t11}{t13}</div>;
    $[34] = t11;
    $[35] = t13;
    $[36] = t14;
  } else {
    t14 = $[36];
  }
  let t15;
  if ($[37] !== contentMaxHeight) {
    t15 = contentMaxHeight ? {
      maxHeight: `${contentMaxHeight}px`
    } : undefined;
    $[37] = contentMaxHeight;
    $[38] = t15;
  } else {
    t15 = $[38];
  }
  let t16;
  if ($[39] !== handleSearchTerm || $[40] !== handleSelectResult || $[41] !== hasQuery || $[42] !== query || $[43] !== searchHistory || $[44] !== searchSettings?.popularSearches || $[45] !== searchSettings?.searchRecommendedItems) {
    t16 = <div className="px-4 tablet:px-16 py-8 tablet:py-12"><div className="mx-auto max-w-7xl">{hasQuery ? <><SearchBoxConnector query={query} /><SearchAutocomplete onSelectResult={handleSelectResult} /></> : <SearchSuggestions suggestions={searchSettings?.searchRecommendedItems ?? null} popularSearches={searchSettings?.popularSearches ?? null} searchHistory={searchHistory} onSearchTerm={handleSearchTerm} />}</div></div>;
    $[39] = handleSearchTerm;
    $[40] = handleSelectResult;
    $[41] = hasQuery;
    $[42] = query;
    $[43] = searchHistory;
    $[44] = searchSettings?.popularSearches;
    $[45] = searchSettings?.searchRecommendedItems;
    $[46] = t16;
  } else {
    t16 = $[46];
  }
  let t17;
  if ($[47] !== t15 || $[48] !== t16) {
    t17 = <div ref={contentRef} className="max-h-[calc(100dvh-155px)] overflow-y-auto overscroll-contain" style={t15}>{t16}</div>;
    $[47] = t15;
    $[48] = t16;
    $[49] = t17;
  } else {
    t17 = $[49];
  }
  let t18;
  if ($[50] !== t14 || $[51] !== t17 || $[52] !== t6) {
    t18 = <div ref={overlayRef} role="dialog" aria-modal="true" aria-label={t6} className="relative z-50 bg-surface-base-primary shadow-lg">{t14}{t17}</div>;
    $[50] = t14;
    $[51] = t17;
    $[52] = t6;
    $[53] = t18;
  } else {
    t18 = $[53];
  }
  let t19;
  if ($[54] !== indexName || $[55] !== t18 || $[56] !== t4 || $[57] !== t5) {
    t19 = <InstantSearchNext searchClient={searchClient} indexName={indexName} insights={t1} future={t2}>{t4}{t5}{t18}</InstantSearchNext>;
    $[54] = indexName;
    $[55] = t18;
    $[56] = t4;
    $[57] = t5;
    $[58] = t19;
  } else {
    t19 = $[58];
  }
  return t19;
}
function _temp() {
  return Math.floor(Date.now() / 1000);
}